import React, { Fragment, useMemo, useState, useEffect, useRef } from 'react'
import { Grid, Cell } from 'react-mdl'

import AddNonEventActivity from './addNonEventActivity'
import RemoveNonEventActivity from './removeNonEventActivity'
import DetailQuestionFields from './detailQuestionFields'

const EventActivitiesForm = ({
  programEvent,
  createdAt,
  endAt,
  instructor,
  allowResultEntry,
  disablePinCheckboxId,
  locale,
}) => {
  const ref = useRef(true)
  const [unsavedChanges, setUnsavedChanges] = useState({})
  const [isPinDisabled, setIsPinDisabled] = useState(false)
  const [eventProgramEvent, setEventProgramEvent] = useState(programEvent)
  const [instructorResultDetails, setInstructorResultDetails] = useState(
    instructor.instructor_result_details
  )
  const [nonEventCount, setNonEventCount] = useState(
    programEvent.event_activities.filter((activity) => activity.non_event_activity).length
  )

  const mergedDetails = useMemo(() => {
    return instructorResultDetails.map((detail) => ({
      ...detail,
      ...(unsavedChanges[detail.id] || {}),
    }))
  }, [instructorResultDetails, unsavedChanges])

  const groupedDetails = useMemo(
    () => Object.groupBy(mergedDetails, (detail) => detail.events_event_activity_id),
    [mergedDetails]
  )

  const existingEventDates = useMemo(() => {
    return eventProgramEvent.event_activities
      .filter(
        (activity) => activity.instructor_id === null || activity.instructor_id === instructor.id
      )
      .map((activity) => activity.start_at)
      .filter(Boolean)
  }, [eventProgramEvent, instructor.id])

  useEffect(() => {
    const checkbox = document.getElementById(disablePinCheckboxId)

    if (checkbox) {
      setIsPinDisabled(checkbox.checked)

      const handleChange = (e) => {
        setIsPinDisabled(e.target.checked)
      }

      checkbox.addEventListener('change', handleChange)
      return () => checkbox.removeEventListener('change', handleChange)
    } else setIsPinDisabled(true)
  }, [disablePinCheckboxId])

  useEffect(() => {
    const count = eventProgramEvent.event_activities.filter(
      (activity) => activity.non_event_activity
    ).length
    setNonEventCount(count)
  }, [eventProgramEvent])

  useEffect(() => {
    const firstRender = ref.current
    const hasInputValues = Object.values(groupedDetails).some((group) =>
      group.some((detail) => detail.value)
    )

    if (firstRender && !hasInputValues) {
      ref.current = false
    }
  }, [instructorResultDetails, groupedDetails])

  const handleDetailChange = (detailId, field, value) => {
    setUnsavedChanges((prev) => ({
      ...prev,
      [detailId]: {
        ...prev[detailId],
        [field]: value,
      },
    }))
  }

  const getDetailValues = (detail, detailIndex) => {
    const savedChanges = unsavedChanges[detail.id] || {}
    return {
      value: savedChanges.value ?? detail.value ?? '',
      [`radioValue_${detailIndex}`]:
        savedChanges[`radioValue_${detailIndex}`] ?? detail.value ?? '',
      height_feet: savedChanges.height_feet ?? detail.calc_height_ft ?? '',
      height_inches: savedChanges.height_inches ?? detail.calc_height_in ?? '',
      checkboxValues:
        savedChanges.checkboxValues ??
        (detail.value && typeof detail.value === 'string' ? detail.value.split(';;') : []),
      weight:
        savedChanges.weight ??
        (detail.value ||
          (locale !== 'en_ca' ? detail.calc_weight_lb : detail.calc_weight_kg) ||
          ''),
    }
  }

  const formEnabled = allowResultEntry && isPinDisabled
  let continuousIndex = 0

  const activityPriority = (activity) =>
    activity.report_detail_question.program_report_detail_questions.find(
      (q) => q.events_program_id === eventProgramEvent.events_program_id
    ).priority

  return (
    <fieldset>
      <AddNonEventActivity
        agencyId={programEvent.events_program.agency_id}
        allowResultEntry={formEnabled}
        instructorId={instructor.id}
        createdAt={createdAt}
        endAt={endAt}
        eventsProgramEventId={eventProgramEvent.id}
        limitReached={nonEventCount >= 10}
        setEventProgramEvent={setEventProgramEvent}
        setInstructorResultDetails={setInstructorResultDetails}
        existingEventDates={existingEventDates}
      />
      {eventProgramEvent.event_activities
        .filter(
          (activity) => activity.instructor_id === null || activity.instructor_id === instructor.id
        )
        .map((activity, eventIdx) => {
          const activityDetails = groupedDetails[activity.id] || []

          return (
            <Fragment key={`event-activity-${activity.id}-${eventIdx}`}>
              <div className="well u-margin--bottom-28">
                <legend className="text--med-lg ml-15 mb-10">
                  <Grid className="mdl-grid--nested">
                    <Cell col={11}>
                      <strong>
                        {activity.full_start_date}
                        {activity.non_event_activity && ' - Non-Event Activity'}
                      </strong>
                    </Cell>
                    <Cell col={1}>
                      {activity.non_event_activity && (
                        <RemoveNonEventActivity
                          id={activity.id}
                          agencyId={eventProgramEvent.events_program.agency_id}
                          hasResults={activityDetails.some((activity) => activity.value)}
                          setEventProgramEvent={setEventProgramEvent}
                          setInstructorResultDetails={setInstructorResultDetails}
                        />
                      )}
                    </Cell>
                  </Grid>
                </legend>
                {activityDetails
                  .sort((a, b) => activityPriority(a) - activityPriority(b))
                  .map((detail) => {
                    const currentIndex = continuousIndex++

                    return (
                      <Fragment key={`detail-${detail.id}-${currentIndex}`}>
                        <input
                          name={`program_event_instructor[instructor_result_details_attributes][${currentIndex}][events_instructor_result_id]`}
                          id={`program_event_instructor_instructor_result_details_attributes_${currentIndex}_events_instructor_result_id`}
                          value={detail.events_instructor_result_id}
                          type="hidden"
                        />
                        <input
                          name={`program_event_instructor[instructor_result_details_attributes][${currentIndex}][events_event_activity_id]`}
                          id={`program_event_instructor_instructor_result_details_attributes_${currentIndex}_events_event_activity_id`}
                          value={detail.events_event_activity_id}
                          type="hidden"
                        />
                        <input
                          name={`program_event_instructor[instructor_result_details_attributes][${currentIndex}][admin_submission]`}
                          id={`program_event_instructor_instructor_result_details_attributes_${currentIndex}_admin_submission`}
                          value="1"
                          type="hidden"
                        />
                        <Grid className="mdl-grid--nested">
                          <Cell col={12}>
                            <DetailQuestionFields
                              key={`detail-field-${detail.id}-${currentIndex}`}
                              detail={detail}
                              locale={locale}
                              defaultValue={detail.report_detail_question.default_value || ''}
                              allowResultEntry={formEnabled}
                              detailIndex={currentIndex}
                              values={getDetailValues(detail, currentIndex)}
                              onDetailChange={handleDetailChange}
                              activityStartDate={activity.start_at}
                            />
                          </Cell>
                        </Grid>
                      </Fragment>
                    )
                  })}
              </div>
            </Fragment>
          )
        })}
      <AddNonEventActivity
        agencyId={programEvent.events_program.agency_id}
        allowResultEntry={formEnabled}
        instructorId={instructor.id}
        createdAt={createdAt}
        endAt={endAt}
        eventsProgramEventId={eventProgramEvent.id}
        limitReached={nonEventCount >= 10}
        setEventProgramEvent={setEventProgramEvent}
        setInstructorResultDetails={setInstructorResultDetails}
        existingEventDates={existingEventDates}
      />
    </fieldset>
  )
}

export default EventActivitiesForm
